import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useNavigate } from "react-router";

import { useMe } from "@/api/me";
import { LEADER_TARGETS, personaHome } from "@/components/layout/nav";
import { useUIStore } from "@/store/uiStore";
import { useViewport } from "@/lib/useViewport";

/**
 * Keyboard (MV2-845, ops/Shortcuts, CROSS_PAGE keyboard leader).
 *
 * Global keys live here; page keys (j/k, e, s, …) stay on their pages.
 * The `g` leader arms for 1.2 s and takes one more key. Nothing fires while
 * the focus is in a field or an overlay is open, except ⌘K, which always
 * opens the palette, and ⌘\ which always toggles the rail.
 */
/**
 * The modifier as the user's keyboard writes it: ⌘ on Apple platforms,
 * Ctrl everywhere else. The handler accepts both regardless.
 */
export const isApplePlatform: boolean =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);
export const MOD = isApplePlatform ? "⌘" : "Ctrl";
/** "⌘K" on a Mac, "Ctrl K" on Windows / Linux. */
export const modKey = (key: string): string =>
  isApplePlatform ? `${MOD}${key}` : `${MOD} ${key}`;

export const KEYMAP: ReadonlyArray<{
  group: string;
  keys: ReadonlyArray<[string, string]>;
}> = [
  {
    group: "Go to",
    keys: [
      ["g o", "Overview"],
      ["g i", "Inbox"],
      ["g a", "Alarms"],
      ["g t", "Tickets"],
      ["g s", "Sites"],
      ["g m", "Map"],
      ["g h", "Home for your persona"],
    ],
  },
  {
    group: "Anywhere",
    keys: [
      [modKey("K"), "Command palette"],
      ["/", "Focus search"],
      ["n", "Notifications"],
      ["1–5", "Bell filter while open"],
      ["?", "This dialog"],
      ["Esc", "Close overlay"],
    ],
  },
  {
    group: "In a list",
    keys: [
      ["j / k", "Next / previous"],
      ["Enter", "Open"],
      ["e", "Acknowledge"],
      ["s", "Snooze"],
      ["x", "Select"],
      [modKey("↵"), "Post comment"],
    ],
  },
  {
    group: "Overview",
    keys: [
      ["1–6", "Jump to tile"],
      ["r", "Refresh fleet"],
    ],
  },
  {
    group: "Analytics",
    keys: [
      ["1–7", "Switch tab"],
      ["d w m q y c", "Period"],
      ["e", "Export CSV"],
    ],
  },
  {
    group: "Map",
    keys: [
      ["f", "Focus mode"],
      ["t", "TV mode"],
      ["Space", "Pause rotation"],
      ["← →", "Previous / next"],
      ["R", "Reset view"],
    ],
  },
  {
    group: "Site detail",
    keys: [
      ["1–9", "Switch tab"],
      ["t", "Raise ticket"],
    ],
  },
  {
    group: "Audit log",
    keys: [
      ["/", "Search"],
      ["e", "Export CSV"],
      ["j / k", "Rows"],
    ],
  },
];

/* ---- overlay state, shared by the header, the menu and the key handler --- */
type Overlay = "palette" | "shortcuts" | null;
let overlay: Overlay = null;
const listeners = new Set<() => void>();
function setOverlay(next: Overlay): void {
  overlay = next;
  listeners.forEach((l) => l());
}
export const shellOverlays = {
  openPalette: () => setOverlay("palette"),
  openShortcuts: () => setOverlay("shortcuts"),
  close: () => setOverlay(null),
  toggle: (which: Exclude<Overlay, null>) =>
    setOverlay(overlay === which ? null : which),
};
export function useShellOverlay(): Overlay {
  return React.useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
    () => overlay,
    () => overlay,
  );
}

export const LEADER_TIMEOUT_MS = 1200;

function inField(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  return (
    tag === "input" ||
    tag === "textarea" ||
    tag === "select" ||
    el.isContentEditable ||
    el.getAttribute("role") === "textbox"
  );
}

/** Mount once inside the router (AppShell). */
export function useGlobalShortcuts(): void {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { isMobile } = useViewport();
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const persona = me?.primary_persona ?? null;
  const leader = React.useRef<number | null>(null);

  React.useEffect(() => {
    const disarm = () => {
      if (leader.current != null) window.clearTimeout(leader.current);
      leader.current = null;
    };
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && !e.altKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        shellOverlays.toggle("palette");
        return;
      }
      if (mod && e.key === "\\") {
        e.preventDefault();
        toggleSidebar();
        return;
      }
      if (mod || e.altKey || inField(e.target) || overlay !== null) return;

      if (leader.current != null) {
        disarm();
        const to =
          e.key === "h"
            ? personaHome(persona, { isMobile })
            : LEADER_TARGETS[e.key];
        if (to) {
          e.preventDefault();
          navigate(to);
        }
        return;
      }
      switch (e.key) {
        case "g":
          leader.current = window.setTimeout(disarm, LEADER_TIMEOUT_MS);
          break;
        case "?":
          e.preventDefault();
          shellOverlays.openShortcuts();
          break;
        case "/":
          e.preventDefault();
          shellOverlays.openPalette();
          break;
        case "n":
          navigate("/notifications");
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      disarm();
    };
  }, [navigate, persona, isMobile, toggleSidebar]);
}

/** A key chip — mono 11px on gray-100, 46px wide. */
export function Kbd({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <kbd className="inline-block min-w-[46px] rounded-[5px] bg-gray-100 px-1.5 py-px text-center font-mono text-[11px] not-italic text-gray-800">
      {children}
    </kbd>
  );
}

/** The `?` dialog — 760 wide, three columns, every key that exists today. */
export function ShortcutsDialog(): React.ReactElement {
  const open = useShellOverlay() === "shortcuts";
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(o) => !o && shellOverlays.close()}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-[color:var(--gray-900)]/40 data-[state=open]:animate-in data-[state=open]:fade-in-0 motion-reduce:animate-none" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 w-[min(760px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 rounded-[14px] border border-gray-200 bg-surface p-[18px] shadow-xl focus:outline-none data-[state=open]:animate-in data-[state=open]:zoom-in-95 motion-reduce:animate-none">
          <div className="flex items-baseline gap-2">
            <DialogPrimitive.Title className="text-[15px] font-semibold text-gray-900">
              Keyboard
            </DialogPrimitive.Title>
            <span className="flex-1" />
            <DialogPrimitive.Description className="type-meta">
              every key here exists in the product today
            </DialogPrimitive.Description>
          </div>
          <div className="mt-3.5 grid grid-cols-1 gap-[18px] sm:grid-cols-2 md:grid-cols-3">
            {KEYMAP.map((g) => (
              <div key={g.group} className="flex flex-col gap-[7px]">
                <div className="type-eyebrow">{g.group}</div>
                {g.keys.map(([k, l]) => (
                  <div key={k} className="flex items-center gap-[11px]">
                    <Kbd>{k}</Kbd>
                    <span className="text-[12.5px] text-gray-800">{l}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
